import { Component, signal, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import {ApiRoom, RoomService} from '../home/services/room.service';
import {ApartmentService, ApiApartment, DocumentDto} from '../home/services/apartment.service';
import { AuthService } from '../../core/services/auth.service';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';

@Component({
  selector: 'app-booking',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, TranslateModule],
  templateUrl: './booking.component.html',
  styleUrls: ['./booking.component.scss']
})
export class BookingComponent implements OnInit {
  checkIn = '2026-03-12';
  checkOut = '2026-03-13';
  adults = signal(2);
  children = signal(0);
  isLoadingRooms = signal(false);
  isLoadingApartments = signal(false);
  rooms = signal<any[]>([]);
  apartments = signal<any[]>([]);
  protected readonly sanitizer = inject(DomSanitizer);

  public authService = inject(AuthService);

  constructor(
    private router: Router,
    private roomService: RoomService,
    private apartmentService: ApartmentService,
    private translate: TranslateService
  ) {
    this.translate.setDefaultLang('fr');
    this.translate.use('fr');
  }

  ngOnInit(): void {
    this.loadAvailabilities();
  }

  isLoading(): boolean {
    return this.isLoadingRooms() || this.isLoadingApartments();
  }

  loadAvailabilities(): void {
    this.loadRooms();
    this.loadApartments();
  }

  private loadRooms(): void {
    this.isLoadingRooms.set(true);
    this.roomService.getRooms(this.checkIn, this.checkOut).pipe(
      finalize(() => this.isLoadingRooms.set(false))
    ).subscribe({
      next: (response) => {
        this.rooms.set(
          (response.content || []).map((room: ApiRoom, index: number) => ({
            id: room.id,
            coverImage: room.coverImage,
            name: room.roomType?.nom || `Chambre ${room.numero}`,
            price: room.prixParNuit,
            rating: 4.5 + Math.random() * 0.5,
            reviews: Math.floor(Math.random() * 200) + 50,
            adults: room.capacite,
            children: Math.floor(room.capacite / 2),
            size: 20 + room.capacite * 10,
            freeCancellation: true,
            paymentType: 'Pay at hotel',
            hotel: room.hotel?.name,
            floor: room.etage,
            status: room.statut,
            capacite: room.capacite,
            numero: room.numero,
            etage: room.etage,
            prixParNuit: room.prixParNuit,
            roomType: room.roomType,
            hotelId: room.hotelId,
            kind: 'room'
          }))
        );
      },
      error: (err) => console.error('[Chambres] Erreur:', err)
    });
  }

  private loadApartments(): void {
    this.isLoadingApartments.set(true);
    this.apartmentService.getApartments(this.checkIn, this.checkOut).pipe(
      finalize(() => this.isLoadingApartments.set(false))
    ).subscribe({
      next: (response) => {
        this.apartments.set(
          (response.content || []).map((apt: ApiApartment, index: number) => ({
            id: apt.id,
            coverImage: apt.coverImage,
            name: apt.name || `Appartement ${apt.numero}`,
            price: apt.prixParNuit,
            rating: 4.5 + Math.random() * 0.5,
            reviews: Math.floor(Math.random() * 200) + 50,
            adults: apt.capacite,
            children: Math.floor(apt.capacite / 2),
            size: apt.numberOfPieces * 20,
            freeCancellation: true,
            paymentType: 'Pay at hotel',
            hotel: apt.apartHotel?.name,
            floor: apt.etage,
            status: apt.statut,
            capacite: apt.capacite,
            numero: apt.numero,
            etage: apt.etage,
            prixParNuit: apt.prixParNuit,
            appartmentType: apt.appartmentType,
            numberOfPieces: apt.numberOfPieces,
            kind: 'apartment'
          }))
        );
      },
      error: (err) => console.error('[Appartements] Erreur:', err)
    });
  }

  hasResults(): boolean {
    return this.rooms().length > 0 || this.apartments().length > 0;
  }

  incrementAdults() {
    this.adults.update(v => v + 1);
  }

  decrementAdults() {
    this.adults.update(v => Math.max(1, v - 1));
  }

  incrementChildren() {
    this.children.update(v => v + 1);
  }

  decrementChildren() {
    this.children.update(v => Math.max(0, v - 1));
  }

  getStars(rating: number): number[] {
    const n = Number(rating) || 0;
    if (!isFinite(n) || n <= 0) return [];
    const count = Math.min(5, Math.max(0, Math.floor(n)));
    return Array.from({ length: count }, (_, i) => i);
  }

  getEmptyStars(rating: number): number[] {
    const n = Number(rating) || 0;
    const filled = isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
    const empty = Math.max(0, 5 - filled);
    return Array.from({ length: empty }, (_, i) => i);
  }

  getImageSrc(images: DocumentDto | undefined, sanitizer: DomSanitizer): SafeUrl | string {
    const img = images;
    if (!img) return ' ';
    if (img.documentUrl) return sanitizer.bypassSecurityTrustUrl(img.documentUrl);
    if (img.content) {
      const mime = img.contentType || 'image/jpeg';
      return sanitizer.bypassSecurityTrustUrl(`data:${mime};base64,${img.content}`);
    }
    return '';
  }

  selectItem(item: any) {
    this.router.navigate(['/booking/details'], {
      state: {
        room: item,
        checkIn: this.checkIn,
        checkOut: this.checkOut,
        adults: this.adults(),
        children: this.children()
      }
    });
  }
}
